interface CloneOptions {
	copySizes: boolean;
	copySpecialSizes: boolean;
	copyColors: boolean;
	copyNotes: boolean;
}

interface ColorSelection {
	colorId: number;
	colorName: string;
	hexCode: string;
	colorOptionId?: number;
	colorPaletteId?: number;
	upCharge?: number;
}

interface SizeSelection {
	sizeId: number;
	name: string;
	quantity: number;
	sizeChartId?: number;
	length?: string;
	sizeLengthId?: number;
}

interface FreeTextSelection {
	colorOptionId: number;
	text: string;
}

interface BaseLineItem {
	itemId: number;
	styleCode: string;
	description: string;
	quantity: number;
	price: number;
	systemCategoryId: number;
	dropShip: boolean;
	stockedItem: boolean;
	stockItems: boolean;
	colorSelections?: ColorSelection[];
	sizeSelections?: SizeSelection[];
	specialSizeSelections?: any[];
	lettering?: any;
	freeTextSelections?: FreeTextSelection[];
	note?: string | null;
	upCharge?: number;
	sizeUpCharge?: number;
	colorUpCharges?: number;
	includePrice?: boolean;
	priceIncluded?: boolean;
	uniqueId?: string;
	type?: string;
	colorOptions?: any[];
	sizeChart?: any;
	braidPosition?: string | null;
	matchPreviousPoNumber?: string | null;
	baseQuantity?: number | null;
	quantityOverride?: number | null;
	isManuallyOverridden?: boolean;
}

// Interface for items in the original order
interface LineItem extends BaseLineItem {
	lineItemId: number;
	children?: LineItem[];
}

// Interface for processed items in the new quote
interface ProcessedLineItem extends BaseLineItem {
	children?: ProcessedLineItem[];
}

interface OrderData {
	salesOrderId: number;
	status: string;
	organization: string;
	customerId: number | null;
	contactId: number | null;
	customerNumber: string | null;
	quoteNumber: string | null;
	orderNumber: string | null;
	schoolPo: string | null;
	billToId: number | null;
	shipToId: number | null;
	shippingMethodId: number | null;
	paymentTermsId: number | null;
	processDate: string | null;
	orderDate: string | null;
	salesRepId: number | null;
	referenceNumber: string | null;
	attribution: string | null;
	salesTaxId: number | null;
	orderTotal: number;
	balance: number;
	shippingAndHandling: number;
	shippingAndHandlingDiscount: number;
	items: LineItem[];
	notes: any[];
	payments: any[];
	quote: {
		quoteNumber: string;
		quoteId: number;
	};
	customer: {
		customer: {
			customerId: number | null;
			organization: string | null;
			addresses: any[];
			contacts: any[];
		};
	};
}

interface QuoteData {
	status: string;
	customerId: number | null;
	contactId: number | null;
	shippingMethodId: number | null;
	paymentTermsId: number | null;
	billToId: number | null;
	shipToId: number | null;
	salesRepId: number | null;
	expectedOrderDate: string | null;
	attribution: string | null;
	schoolPo: string | null;
	referenceNumber: string | null;
	shippingAndHandling: number | null;
	shippingAndHandlingDiscount: number | null;
	items: ProcessedLineItem[];
	notes: any[];
	payments: any[];
}

const state = {
	clonedQuote: null as QuoteData | null,
};

const getters = {
	clonedQuote: (state: { clonedQuote: QuoteData | null }) => state.clonedQuote,
};

const mutations = {
	setClonedQuote: (state: { clonedQuote: QuoteData | null }, payload: QuoteData) => {
		state.clonedQuote = payload;
	},
	clearClonedQuote: (state: { clonedQuote: QuoteData | null }) => {
		state.clonedQuote = null;
	},
};

const actions = {
	async cloneOrderToQuote(
		{ commit }: { commit: Function },
		{ orderData, options }: { orderData: OrderData; options: CloneOptions }
	): Promise<QuoteData> {
		try {
			const processColorSelections = (colorSelections: ColorSelection[] = []): ColorSelection[] => {
				return colorSelections.map((selection) => ({
					colorId: selection.colorId,
					colorName: selection.colorName,
					hexCode: selection.hexCode,
					colorOptionId: selection.colorOptionId,
					colorPaletteId: selection.colorPaletteId,
					upCharge: selection.upCharge,
				}));
			};

			const processSizeSelections = (
				sizeSelections: SizeSelection[] = [],
				sizeChart?: any
			): SizeSelection[] => {
				const chartSizes: any[] = sizeChart?.sizes ?? [];
				return sizeSelections.map((selection) => {
					// #1392: a line's size selections must reference its OWN item's chart. Sibling
					// styles (e.g. TFBS4-WHT vs -RBL) share size names but not SizeIds, so a source
					// order carrying a stale sibling SizeId would otherwise propagate through every
					// reorder. The cloned chart is already this item's, so re-key the selection to it
					// by name (and length). Falls back to the original ids when no name match exists.
					const match = chartSizes.find((s) => s.name === selection.name);
					let sizeId = selection.sizeId;
					let sizeLengthId = selection.sizeLengthId;
					if (match && match.sizeId !== selection.sizeId) {
						sizeId = match.sizeId;
						if (selection.sizeLengthId != null && Array.isArray(match.lengths)) {
							const lenMatch = match.lengths.find((l: any) => l.length === selection.length);
							sizeLengthId = lenMatch ? lenMatch.sizeLengthId : selection.sizeLengthId;
						}
					}
					return {
						sizeId,
						name: selection.name,
						quantity: selection.quantity,
						sizeChartId: sizeChart?.sizeChartId ?? selection.sizeChartId,
						length: selection.length,
						sizeLengthId,
					};
				});
			};

			// Process line items recursively to maintain hierarchy
			const processLineItems = (items: LineItem[]): ProcessedLineItem[] => {
				return items.map((item) => {
					const processedItem: ProcessedLineItem = {
						itemId: item.itemId,
						styleCode: item.styleCode,
						description: item.description,
						quantity: item.quantity,
						// Preserve manual sub-item quantity overrides — without these flags,
						// updateItemQuantities re-derives child quantities from the parent and
						// discards the override (e.g. 1-of-2 decorated) on the cloned quote
						baseQuantity: item.baseQuantity,
						quantityOverride: item.quantityOverride,
						isManuallyOverridden: item.isManuallyOverridden ?? false,
						price: item.price,
						systemCategoryId: item.systemCategoryId,
						dropShip: item.dropShip,
						stockedItem: item.stockedItem,
						stockItems: item.stockedItem || item.stockItems,
						colorSelections: options.copyColors
							? processColorSelections(item.colorSelections) || []
							: [],
						// Match Previous PO # is entered in the Color Options dialog, so it
						// rides along with the color selections option (#1430)
						matchPreviousPoNumber: options.copyColors ? item.matchPreviousPoNumber ?? null : null,
						sizeSelections: options.copySizes
							? processSizeSelections(item.sizeSelections, item.sizeChart) || []
							: [],
						specialSizeSelections: options.copySpecialSizes
							? (item.specialSizeSelections || []).map((s) => ({
									size: s.size,
									length: s.length,
									quantity: s.quantity,
							  }))
							: [],
						lettering: item.lettering,
						freeTextSelections: options.copyColors
							? (item.freeTextSelections || []).map((t) => ({
									colorOptionId: t.colorOptionId,
									text: t.text,
							  }))
							: [],
						note: options.copyNotes ? item.note : null,
						upCharge: item.upCharge,
						sizeUpCharge: options.copySizes ? item.sizeUpCharge : 0,
						colorUpCharges: options.copyColors ? item.colorUpCharges : 0,
						includePrice: item.includePrice,
						priceIncluded: item.priceIncluded,
						uniqueId: `clone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
						type: item.type,
						colorOptions: item.colorOptions ? [...item.colorOptions] : [],
						sizeChart: item.sizeChart ? { ...item.sizeChart } : undefined,
						braidPosition: item.braidPosition,
					};

					// Process children recursively if they exist
					if (item.children && item.children.length > 0) {
						processedItem.children = processLineItems(item.children);
					}

					return processedItem;
				});
			};

			// Create the cloned quote data structure
			const clonedQuote: QuoteData = {
				status: "INCOMPLETE",
				customerId: null,
				contactId: null,
				shippingMethodId: null,
				paymentTermsId: null,
				billToId: null,
				shipToId: null,
				salesRepId: null,
				expectedOrderDate: null,
				attribution: orderData.attribution,
				schoolPo: orderData.schoolPo,
				referenceNumber: null,
				shippingAndHandling: 0,
				shippingAndHandlingDiscount: 0,
				items: processLineItems(orderData.items),
				notes: [], // Start with empty notes for the new quote
				payments: [], // Start with empty payments for the new quote
			};

			commit("setClonedQuote", clonedQuote);
			return clonedQuote;
		} catch (error) {
			console.error("Error cloning order to quote:", error);
			throw error;
		}
	},

	clearClonedQuote({ commit }: { commit: Function }) {
		commit("clearClonedQuote");
	},
};

export default {
	state,
	getters,
	mutations,
	actions,
};
